import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { BadgeCheck, Lock, ShieldCheck, Sparkles } from 'lucide-react';
import { build, site, nav } from '../../config/site';

const COMPLIANCE = [
  { label: '18+ only', detail: 'Age-gated access' },
  { label: 'Provably fair', detail: 'Verifiable RNG' },
  { label: 'SOC 2 controls', detail: 'Audit-ready' },
  { label: 'HIPAA safeguards', detail: 'Where applicable' },
];

export function Footer() {
  return (
    <footer className="mt-16 border-t border-white/8 bg-ink-1000/60">
      <div className="mx-auto max-w-[1600px] px-4 py-12 sm:px-6">
        <div className="grid gap-10 lg:grid-cols-[1.4fr_repeat(3,1fr)]">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="grid size-9 place-items-center rounded-xl bg-gradient-to-br from-agave-400 to-agave-700">
                <Sparkles className="size-5 text-ink-1000" />
              </span>
              <span className="font-display text-xl font-extrabold text-white">{site.name}</span>
            </div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-ink-400">{site.description}</p>
            <div className="mt-5 grid grid-cols-2 gap-2">
              {COMPLIANCE.map((item) => (
                <div
                  key={item.label}
                  className="flex items-start gap-2 rounded-lg border border-white/8 bg-white/4 p-2.5"
                >
                  <ShieldCheck className="mt-0.5 size-4 shrink-0 text-agave-400" />
                  <span>
                    <span className="block text-xs font-semibold text-white">{item.label}</span>
                    <span className="block text-[10px] text-ink-400">{item.detail}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <FooterColumn title="Casino" links={nav.footer.casino} />
          <FooterColumn title="Account" links={nav.footer.account} />
          <FooterColumn title="Legal" links={nav.footer.legal} />
        </div>

        <div className="mt-10 flex flex-col gap-4 border-t border-white/8 pt-6 text-xs text-ink-500 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} {site.legalName}. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <span className="flex items-center gap-1.5">
              <Lock className="size-3.5 text-agave-400" /> Encrypted in transit &amp; at rest
            </span>
            <span className="flex items-center gap-1.5">
              <BadgeCheck className="size-3.5 text-agave-400" /> Built on AWS serverless
            </span>
            <Badge tone={build.environment}>
              {build.environment}
              {build.commit !== 'dev' ? ` · ${build.commit.slice(0, 7)}` : ''}
            </Badge>
          </div>
        </div>

        <p className="mt-6 max-w-4xl text-[11px] leading-relaxed text-ink-600">
          Agave is a social casino intended for entertainment only. It does not offer real-money
          gambling or the opportunity to win real money or prizes. Virtual credits have no monetary
          value and cannot be exchanged, transferred or redeemed. Practice or success at social
          casino gaming does not imply future success at real-money gambling. Access is restricted
          to players aged {site.minimumAge} and over. If gambling is affecting your life, seek
          support from your local responsible-gambling service.
        </p>
      </div>
    </footer>
  );
}

function FooterColumn({
  title,
  links,
}: {
  title: string;
  links: readonly { label: string; to: string }[];
}) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-300">{title}</h3>
      <ul className="mt-4 space-y-2.5">
        {links.map((link) => (
          <li key={link.to}>
            <Link
              to={link.to}
              className="text-sm text-ink-400 transition-colors hover:text-agave-300"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Badge({ tone, children }: { tone: string; children: ReactNode }) {
  const tones: Record<string, string> = {
    prod: 'border-agave-500/40 bg-agave-500/10 text-agave-300',
    stage: 'border-gold-500/40 bg-gold-500/10 text-gold-300',
    dev: 'border-sky-450/40 bg-sky-450/10 text-sky-450',
    local: 'border-white/15 bg-white/5 text-ink-300',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tones[tone] ?? tones.local}`}
    >
      {children}
    </span>
  );
}
